import React, { useEffect, useState } from 'react';

import { Choice, Group, RadioChoice, Toggle } from './OptionControls';

const TOP_LIMITS = [5, 10, 15];

const HORIZONS = [30, 60, 90];

const MAX_THRESHOLD = 1000000;

/**
 * The contract value threshold, in thousands of the event currency. Once the
 * field loses focus an emptied value reads as 0 and one out of range is
 * brought within 0 to MAX_THRESHOLD.
 */
const ThresholdInput = ({ value, currencyCode, disabled, onChange }) => {
    const [draft, setDraft] = useState(String(value ?? 0));

    // Follows outside changes (a loaded configuration) without overwriting a
    // number still being typed.
    useEffect(() => {
        setDraft((current) => (current !== '' && Number(current) === Number(value ?? 0) ? current : String(value ?? 0)));
    }, [value]);

    const label = currencyCode
        ? $t('Contract value threshold ({{currency}} thousands)', { currency: currencyCode })
        : $t('Contract value threshold (thousands)');

    return (
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
            <label className="tw-text-sm tw-text-charcoal tw-mb-0" htmlFor="mp-contracts-threshold">
                {label}
            </label>
            <div className="tw-w-44 tw-shrink-0">
                <input
                    id="mp-contracts-threshold"
                    type="number"
                    className="text-input tw-border-0 tw-w-full"
                    min={0}
                    max={MAX_THRESHOLD}
                    step="any"
                    value={draft}
                    disabled={disabled}
                    onChange={(event) => {
                        const number = Number(event.target.value);
                        setDraft(event.target.value);

                        if (event.target.value !== '' && !Number.isNaN(number) && number >= 0 && number <= MAX_THRESHOLD) {
                            onChange(number);
                        }
                    }}
                    onBlur={() => {
                        const number = draft === '' || Number.isNaN(Number(draft)) ? 0 : Number(draft);
                        const clamped = Math.min(Math.max(number, 0), MAX_THRESHOLD);

                        if (String(clamped) !== draft) {
                            setDraft(String(clamped));
                        }

                        if (clamped !== value) {
                            onChange(clamped);
                        }
                    }}
                />
            </div>
        </div>
    );
};

/**
 * The contract management section's options: which contracts the page covers,
 * how many top contracts it lists and whether with their supplier, the value
 * threshold, and how far ahead it looks for milestones.
 */
const ContractsOptions = ({ options, currencyCode, readOnly, onChange }) => {
    const set = (changes) => onChange({ ...options, ...changes });

    return (
        <div className="tw-flex tw-flex-col tw-mt-3 tw-ps-4 tw-border-s-2 tw-border-charcoal-15">
            <Group title={$t('Contracts')}>
                <RadioChoice
                    name="mp-contracts-type"
                    label={$t('Contracts')}
                    value={options.type}
                    choices={[
                        { value: 'expenditure', label: $t('Expenditure') },
                        { value: 'revenue', label: $t('Revenue') },
                    ]}
                    disabled={readOnly}
                    onChange={(type) => set({ type })}
                />
                <Choice
                    id="mp-contracts-top-limit"
                    label={$t('Contracts shown')}
                    value={options.top_limit}
                    choices={TOP_LIMITS.map((limit) => ({ value: limit, label: $t('Top {{count}}', { count: limit }) }))}
                    disabled={readOnly}
                    onChange={(value) => set({ top_limit: value })}
                />
                <ThresholdInput
                    value={options.value_threshold}
                    currencyCode={currencyCode}
                    disabled={readOnly}
                    onChange={(value) => set({ value_threshold: value })}
                />
                <Toggle
                    label={$t('Open contracts only')}
                    checked={!!options.open_only}
                    disabled={readOnly}
                    onChange={() => set({ open_only: !options.open_only })}
                />
                <Toggle
                    label={$t('Supplier column')}
                    hint={$t('The supplier or counterparty of each top contract')}
                    checked={!!options.show_supplier}
                    disabled={readOnly}
                    onChange={() => set({ show_supplier: !options.show_supplier })}
                />
                <Choice
                    id="mp-contracts-horizon"
                    label={$t('Milestone horizon')}
                    value={options.horizon_days}
                    choices={HORIZONS.map((days) => ({ value: days, label: $t('{{count}} days', { count: days }) }))}
                    disabled={readOnly}
                    onChange={(value) => set({ horizon_days: value })}
                />
            </Group>
        </div>
    );
};

export default ContractsOptions;
